/**
 * Fantasy Console 0 - Play Page
 *
 * Standalone page for playing shared carts via /play/:cartId URL.
 * Minimal player view without editor interface.
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { CartService, CartWithStats } from '../services/cartService'
import { ConsoleWrapper } from '../components/Console/ConsoleWrapper'
import { useToast } from '../context/ToastContext'
import { ToastContainer } from '../components/Toast/ToastContainer'
import {
  saveCurrentCart,
  saveCloudSaveHash,
  loadCurrentCart,
  loadCloudSaveHash,
} from '../utils/storage'
import styles from './PlayPage.module.css'

type PlayPageState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'loaded'; cart: CartWithStats }

export function PlayPage() {
  const { cartId } = useParams<{ cartId: string }>()
  const { success: showSuccess } = useToast()
  const navigate = useNavigate()
  const [state, setState] = useState<PlayPageState>({ status: 'loading' })

  const fetchCart = useCallback(() => {
    if (!cartId) {
      setState({ status: 'error', message: 'No cart ID provided' })
      return
    }

    setState({ status: 'loading' })
    let cancelled = false

    CartService.loadCartWithUsername(cartId)
      .then((cart) => {
        if (!cancelled) {
          setState({ status: 'loaded', cart })
        }
      })
      .catch((err) => {
        if (!cancelled) {
          const message =
            err instanceof Error ? err.message : 'Failed to load cart'
          setState({ status: 'error', message })
        }
      })

    return () => {
      cancelled = true
    }
  }, [cartId])

  useEffect(() => {
    return fetchCart()
  }, [fetchCart])

  // Set page title when cart loads
  useEffect(() => {
    if (state.status === 'loaded') {
      document.title = `${state.cart.metadata.title} - Fantasy Console 0`
    }
    return () => {
      document.title = 'Fantasy Console 0'
    }
  }, [state])

  const cart = state.status === 'loaded' ? state.cart : undefined
  const author = cart
    ? cart.username || cart.metadata.author || 'Unknown'
    : undefined

  const handleOpenInEditor = useCallback(() => {
    if (!cart) {
      window.location.href = '/'
      return
    }

    // Check if the editor has unsaved cloud changes for a different cart
    const existingCart = loadCurrentCart()
    if (existingCart && existingCart.id && existingCart.id !== cart.id) {
      const existingHash = JSON.stringify({
        codeFiles: existingCart.codeFiles,
        sprites: existingCart.sprites,
        map: existingCart.map,
        sfx: existingCart.sfx,
        metadata: existingCart.metadata,
      })
      const savedHash = loadCloudSaveHash()
      if (savedHash && savedHash !== existingHash) {
        const confirmed = window.confirm(
          'You have unsaved cloud changes on another cart. Opening this cart will discard them. Continue?',
        )
        if (!confirmed) return
      }
    }

    saveCurrentCart(cart)
    const hash = JSON.stringify({
      codeFiles: cart.codeFiles,
      sprites: cart.sprites,
      map: cart.map,
      sfx: cart.sfx,
      metadata: cart.metadata,
    })
    saveCloudSaveHash(hash)
    navigate('/', { state: { direct: true } })
  }, [cart, navigate])

  const handleShare = useCallback(async () => {
    await navigator.clipboard.writeText(window.location.href)
    showSuccess('Link copied to clipboard!')
  }, [showSuccess])

  // Full-page error states (no cart ID, not found, etc.)
  if (state.status === 'error') {
    const isNotFound = state.message === 'Cart not found'
    return (
      <div className={styles.errorContainer}>
        <h1 className={styles.errorTitle}>
          {isNotFound ? "This cart doesn't exist" : 'Failed to load cart'}
        </h1>
        <p className={styles.errorMessage}>{state.message}</p>
        <div className={styles.errorActions}>
          {!isNotFound && (
            <button className='btn btn-primary' onClick={fetchCart}>
              Try Again
            </button>
          )}
          <a href='/' className='btn btn-secondary'>
            Back to Editor
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <header className={styles.topBar}>
        <a href='/' className={styles.logo}>
          FC0
        </a>
        <span className={styles.tagline}>
          Made with{' '}
          <a href='/' className={styles.taglineLink}>
            Fantasy Console 0
          </a>
        </span>
      </header>

      <main className={styles.main}>
        <div className={styles.consoleArea}>
          <ConsoleWrapper
            cart={cart}
            size='xLarge'
            autoLoad={true}
            autoReloadOnCodeChange={false}
            showBootAnimation={true}
          />
        </div>

        <div className={styles.cartInfo}>
          {cart ? (
            <>
              <h1 className={styles.title}>{cart.metadata.title}</h1>
              <p className={styles.author}>by {author}</p>
              {cart.metadata.description && (
                <p className={styles.description}>
                  {cart.metadata.description}
                </p>
              )}
              <div className={styles.actions}>
                <button
                  className='btn btn-primary btn-sm'
                  onClick={handleOpenInEditor}
                >
                  Open in Editor
                </button>
                <button
                  className='btn btn-secondary btn-sm'
                  onClick={handleShare}
                >
                  <ShareIcon />
                  <span>Share</span>
                </button>
              </div>
            </>
          ) : (
            <>
              <div className={`${styles.skeleton} ${styles.skeletonTitle}`} />
              <div className={`${styles.skeleton} ${styles.skeletonAuthor}`} />
              <div
                className={`${styles.skeleton} ${styles.skeletonDescription}`}
              />
            </>
          )}
        </div>

        <div className={styles.controls}>
          <h3 className={styles.controlsTitle}>Controls</h3>
          <div className={styles.controlsList}>
            <div className={styles.controlGroup}>
              <span className={styles.controlLabel}>D-Pad</span>
              <div className={styles.keys}>
                <kbd>Arrow Keys / WASD</kbd>
              </div>
            </div>
            <div className={styles.controlGroup}>
              <span className={styles.controlLabel}>B Button</span>
              <div className={styles.keys}>
                <kbd>Z / J</kbd>
              </div>
            </div>
            <div className={styles.controlGroup}>
              <span className={styles.controlLabel}>A Button</span>
              <div className={styles.keys}>
                <kbd>X / K</kbd>
              </div>
            </div>
            <div className={styles.controlGroup}>
              <span className={styles.controlLabel}>Start</span>
              <div className={styles.keys}>
                <kbd>Space / Enter</kbd>
              </div>
            </div>
            <div className={styles.controlGroup}>
              <span className={styles.controlLabel}>Select</span>
              <div className={styles.keys}>
                <kbd>Shift</kbd>
              </div>
            </div>
          </div>
        </div>
      </main>

      <ToastContainer />
    </div>
  )
}

function ShareIcon() {
  return (
    <svg
      width='14'
      height='14'
      viewBox='0 0 14 14'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <circle cx='10.5' cy='2.5' r='1.5' />
      <circle cx='3.5' cy='7' r='1.5' />
      <circle cx='10.5' cy='11.5' r='1.5' />
      <path d='M5 7.9l4 2.7M5 6.1l4-2.7' />
    </svg>
  )
}
